/**
 * EPI Filters Component
 * Inline filter bar: filters stay visible and apply live (no popover).
 */
import React from 'react';
import { InlineFilterBar, InlineSelect, InlineToggles } from '../../../components/common';
import { useFilterState } from '../../../hooks/useFilterState';
import { EpiStatus, TipoDeRisco } from '../../../types/epi';

export interface EpiFiltersData {
  searchTerm: string;
  tipoDeRisco: string;
  status: string;
  caExpired: boolean;
  caExpiringSoon: boolean;
}

interface Props {
  onFilter: (filters: EpiFiltersData) => void;
  loading?: boolean;
  total?: number;
}

const RISK_LABELS: Record<string, string> = {
  [TipoDeRisco.FISICO]: 'Físico',
  [TipoDeRisco.QUIMICO]: 'Químico',
  [TipoDeRisco.BIOLOGICO]: 'Biológico',
  [TipoDeRisco.ERGONOMICO]: 'Ergonômico',
  [TipoDeRisco.ACIDENTE]: 'Acidente',
};

const STATUS_LABELS: Record<string, string> = {
  [EpiStatus.ATIVO]: 'Ativo',
  [EpiStatus.INATIVO]: 'Inativo',
};

const RISK_OPTIONS = Object.values(TipoDeRisco).map((t) => ({
  value: t,
  label: RISK_LABELS[t],
}));

const STATUS_OPTIONS = Object.values(EpiStatus).map((s) => ({
  value: s,
  label: STATUS_LABELS[s],
}));

const defaultValues: EpiFiltersData = {
  searchTerm: '',
  tipoDeRisco: '',
  status: '',
  caExpired: false,
  caExpiringSoon: false,
};

export const EPIFiltersComponent: React.FC<Props> = ({ onFilter, loading = false, total }) => {
  const { filters, setFilter, clearAll, activeChips } = useFilterState(defaultValues, {
    onFilter,
    applyOnMount: true,
    live: true,
    labels: {
      tipoDeRisco: 'Risco',
      status: 'Status',
    },
    formatValue: {
      tipoDeRisco: (v: string) => RISK_LABELS[v] ?? v,
      status: (v: string) => STATUS_LABELS[v] ?? v,
    },
  });

  return (
    <InlineFilterBar
      searchPlaceholder="Buscar por nome, risco, C.A...."
      searchValue={filters.searchTerm}
      onSearchChange={(v) => setFilter('searchTerm', v)}
      resultCount={total}
      resultNoun="EPIs"
      chips={activeChips}
      onClearAll={clearAll}
      loading={loading}
    >
      <InlineSelect
        label="Tipo de Risco"
        value={filters.tipoDeRisco}
        options={RISK_OPTIONS}
        onChange={(v) => setFilter('tipoDeRisco', v)}
        emptyLabel="Todos"
        minWidth={160}
      />
      <InlineSelect
        label="Status"
        value={filters.status}
        options={STATUS_OPTIONS}
        onChange={(v) => setFilter('status', v)}
        emptyLabel="Todos"
        minWidth={140}
      />
      <InlineToggles
        disabled={loading}
        items={[
          {
            label: 'CA Vencido',
            checked: filters.caExpired,
            onChange: (v) => setFilter('caExpired', v),
          },
          {
            label: 'CA Vencendo (30 dias)',
            checked: filters.caExpiringSoon,
            onChange: (v) => setFilter('caExpiringSoon', v),
          },
        ]}
      />
    </InlineFilterBar>
  );
};

export default EPIFiltersComponent;
